import React, { useState } from "react";
import {
  Button,
  message,
  Space,
  Table,
  Tag,
  Typography,
  Modal,
  Input,
  Tooltip,
  Col,
  Row,
} from "antd";
import { CheckOutlined, CloseCircleOutlined, CloseOutlined, InfoCircleOutlined, PauseOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import { updateNurseCommonData } from "../../../services/nurse.services";
import { capitalizeFirstLetter } from "../../../helpers/utility";
import dayjs from "dayjs";
import Radio from "antd/es/radio/radio";
import { reasonKeys } from "../../../helpers/medicineKeys";
import useIsMobile from "../../../hooks/useIsMobile";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../IpEMR/Doctor/InitialInspection/ClosePopup";
import { releaseNurseMobileScrollLock } from "./releaseNurseMobileScrollLock";
import "./NurseMedicine.css";

const MedActionButton = ({ onClick, ariaLabel, children, variant = "default" }) => (
  <button
    type="button"
    className={`nurse-med-action-btn nurse-med-action-btn--${variant}`}
    onClick={onClick}
    aria-label={ariaLabel}
  >
    {children}
  </button>
);
const COL_MEDICINE_OR_DRUG = 340;
const COL_DOCTOR_COMMENT = 200;
const COL_TIME_SLOT = 170;

const ScheduledMedicine = ({
  medData,
  statData,
  sortedTimeSlots,
  refreshPage,
  date,
  curDate,
  loading,
  timingsRegionWidth: timingsRegionWidthProp,
}) => {
  const [editing, setEditing] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [visibleModal, setVisibleModal] = useState(false);
  const [currentAction, setCurrentAction] = useState({});
  const [selectedReason, setSelectedReason] = useState(null);
  const isMobile = useIsMobile();
  const MOBILE_TIME_COL_MIN = 200;

  const handleSubmit = (item, action) => {
    const updatedMedicine = { action: action?.action };
    updateNurseCommonData(item?.id, {
      ...updatedMedicine,
      remarks: inputValue,
      reason: selectedReason,
    })
      .then((res) => {
        message.success(res.data.message);
        refreshPage();
        setVisibleModal(false);
        setEditing({});
      })
      .catch((err) => {
        message.error(err.response.data.message);
      });
  };

  const handleEditClick = (id, hour) => {
    setEditing({
      [id]: {
        [hour]: true,
      },
    });
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const showModal = (medicine, action) => {
    setCurrentAction({ medicine: medicine, action: action });
    setInputValue(medicine?.remarks || "");
    setSelectedReason(medicine?.nurse_data?.reason || null);
    setVisibleModal(true);
  };

  const resetModalState = () => {
    setInputValue("");
    setSelectedReason(null);
    setCurrentAction({});
  };

  const handleCloseModal = () => {
    setVisibleModal(false);
  };

  const handleModalOpenChange = (open) => {
    if (!open) {
      resetModalState();
      if (isMobile) {
        releaseNurseMobileScrollLock();
      }
    }
  };

  const handleModalSubmit = () => {
    handleSubmit(currentAction?.medicine, currentAction?.action);
  };

  const nTimeSlots = Array.isArray(sortedTimeSlots) ? sortedTimeSlots.length : 0;
  const timingsRegionWidth =
    typeof timingsRegionWidthProp === "number" && timingsRegionWidthProp > 0
      ? timingsRegionWidthProp
      : nTimeSlots * COL_TIME_SLOT;
  const timeColumnWidth =
    nTimeSlots > 0
      ? Math.max(
          isMobile ? MOBILE_TIME_COL_MIN : 0,
          timingsRegionWidth / nTimeSlots
        )
      : COL_TIME_SLOT;

  const commonButtons = (action) => (
    <>
      <MedActionButton
        variant="done"
        onClick={() => showModal(action, { action: "done" })}
        ariaLabel="Mark done"
      >
        <CheckOutlined />
      </MedActionButton>
      <MedActionButton
        variant="pause"
        onClick={() => showModal(action, { action: "withheld" })}
        ariaLabel="Withhold"
      >
        <PauseOutlined />
      </MedActionButton>
      <MedActionButton
        variant="cancel"
        onClick={() => showModal(action, { action: "not_done" })}
        ariaLabel="Not done"
      >
        <CloseOutlined />
      </MedActionButton>
    </>
  );

  const columns = [
    {
      title: "Medicine Name",
      dataIndex: "name",
      key: "name",
      width: COL_MEDICINE_OR_DRUG,
      ...(isMobile ? {} : { fixed: "left" }),
      render: (text, record) => (
        <div style={{ minWidth: isMobile ? 200 : 260 }}>
          <Typography style={{ fontWeight: 500, fontSize: 12 }}>
            {record?.name || record?.medicine_name}
          </Typography>
          <Space>
            {record?.drug && (
              <Typography style={{ fontSize: "10px", color: "#667085" }}>
                Drug: {record?.drug}
              </Typography>
            )}
            {record?.dose && (
              <Typography style={{ fontSize: "10px", color: "#667085" }}>
                Dose: {record?.dose}
              </Typography>
            )}
            {record?.route && (
              <Typography style={{ fontSize: 10, color: "#667085" }}>
                Route: {record?.route}
              </Typography>
            )}
            {record?.days_left_adjusted && (
              <Typography style={{ fontSize: 10, color: "#667085" }}>
                Duration: {record?.days_left_adjusted} days
              </Typography>
            )}
          </Space>
        </div>
      ),
    },
    {
      title: "Doctor's Comment",
      dataIndex: "comments",
      key: "comments",
      width: COL_DOCTOR_COMMENT,
      ...(isMobile ? {} : { fixed: "left" }),
      render: (text, record) => record?.comments ?? "-",
      align: "center",
    },
    ...(nTimeSlots > 0
      ? sortedTimeSlots.map((slot, slotIndex) => {
      const timeCellClass =
        "nurse-med-action-cell" +
        (slotIndex < nTimeSlots - 1 ? " nurse-med-action-cell--divider" : "");

      return {
      title: dayjs(String(slot), "HH").format("hh A"),
      dataIndex: slot,
      key: slot,
      width: timeColumnWidth,
      align: "center",
      onHeaderCell: () => ({ className: timeCellClass }),
      onCell: () => ({ className: timeCellClass }),

      render: (_, record) => {
        const action = record?.actions?.find(
          (act) => act?.hour === slot && act?.is_stopped !== true
        );

        const isEditing = editing[action?.id]?.[slot];

        return (
          <>
            {isEditing ? (
              <div className="nurse-med-action-slot">
                <div className="nurse-med-action-group">
                  {commonButtons(action)}
                  <button
                    type="button"
                    className="nurse-med-action-cancel"
                    onClick={() => setEditing({})}
                    aria-label="Cancel edit"
                  >
                    <CloseCircleOutlined style={{ color: "red" }} />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Row gutter={16} justify="center">
                  <Col span={24}>
                    {action?.processed === false ? (
                      <div className="nurse-med-action-slot">
                        <div className="nurse-med-action-group">
                          {commonButtons(action)}
                        </div>
                      </div>
                    ) : action?.processed === true ? (
                      <Space>
                        <Tag
                          color={
                            action?.action === "not_done"
                              ? "red"
                              : action?.action === "withheld"
                              ? "orange"
                              : "green"
                          }
                        >
                          {capitalizeFirstLetter(
                            action?.action?.replace("_", " ")
                          )}
                        </Tag>

                        {date === curDate && (
                          <Button
                            size="small"
                            onClick={() => handleEditClick(action?.id, slot)}
                          >
                            <EditIcon />
                          </Button>
                        )}
                        <Tooltip
                          title={
                            <>
                              <Typography style={{ color: "#fff" }}>
                                Updated By : {action?.updated_by}
                              </Typography>
                              <Typography style={{ color: "#fff" }}>
                                Updated At :{" "}
                                {dayjs(action?.updated_time)?.format("hh:mm A")}
                              </Typography>
                            </>
                          }
                        >
                          <InfoCircleOutlined style={{ color: "blue" }} />
                        </Tooltip>
                      </Space>
                    ) : null}
                  </Col>
                  <Col span={24} style={{ padding: "0px 60px" }}>
                    <div style={{ height: 8 }} />
                    <Typography style={{ fontSize: "10px" }}>
                      {action?.nurse_data?.reason !== "Others"
                        ? action?.nurse_data?.reason
                        : action?.remarks}
                    </Typography>
                  </Col>
                </Row>
              </>
            )}
          </>
        );
      },
    };
    })
      : timingsRegionWidth > 0
        ? [
            {
              title: "",
              key: "__timings_spacer__",
              width: timingsRegionWidth,
              render: () => null,
            },
          ]
        : []),
  ];

  const scrollX =
    COL_MEDICINE_OR_DRUG +
    COL_DOCTOR_COMMENT +
    (isMobile && nTimeSlots > 0
      ? nTimeSlots * MOBILE_TIME_COL_MIN
      : timingsRegionWidth);

  return (
    <div>
      <div className="nurse-med-table-heading">
        Scheduled Medicines
      </div>
      <Table
        size="small"
        className="scheduled-medicine-table"
        dataSource={medData}
        columns={columns}
        pagination={false}
        bordered
        tableLayout="fixed"
        scroll={
          isMobile
            ? { x: scrollX }
            : { y: "calc(100vh - 300px)", x: scrollX }
        }
        loading={loading}
      />

      <Modal
        title="Enter Reason"
        open={visibleModal}
        onCancel={handleCloseModal}
        afterOpenChange={handleModalOpenChange}
        className="nurse-med-reason-modal"
        {...(isMobile && {
          centered: true,
          wrapClassName: NURSE_IP_COMPACT_MODAL_WRAP,
          getContainer: () => document.body,
          width: "calc(100vw - 32px)",
          destroyOnClose: true,
        })}
        footer={
          <Space>
            <Button type="primary" onClick={handleModalSubmit}>
              Submit
            </Button>
          </Space>
        }
      >
        {currentAction?.action?.action !== "done" && (
          <Row gutter={[16, isMobile ? 8 : 12]}>
            {reasonKeys?.map((reason) => (
              <Col span={isMobile ? 24 : 12} key={reason.value}>
                <Radio
                  checked={selectedReason === reason.value}
                  onChange={() => setSelectedReason(reason.value)}
                >
                  {reason.label}
                </Radio>
              </Col>
            ))}
          </Row>
        )}

        {(currentAction?.action?.action === "done" ||
          selectedReason === "Others") && (
          <div style={{ marginTop: isMobile ? 12 : 16 }}>
            <Input
              value={inputValue}
              onChange={handleInputChange}
              placeholder="Enter reason for action"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ScheduledMedicine;
